/**
 * transcriptRowVisibility — which rows of a thread the transcript DRAWS
 * (2026-09-15), as one predicate the render loop and the "new messages" rule
 * both read.
 *
 * The finding: seven "new messages" rules stacked above the first visible row
 * of a group thread (Miles's screenshot). The rule was pushed for every row
 * whose key matched the frozen anchor, BEFORE the loop's own skips — and the
 * anchor walk knew two of the six kinds of row the loop hides, so a hidden
 * anchor row drew a rule with nothing under it, and rows sharing the anchor's
 * key each drew another. Two definitions of "does this row render" is how the
 * rule and the transcript came to disagree about the same message.
 *
 * Leaf on purpose: pure predicates over the message shape, no React, no DOM,
 * so the rule is pinnable without rendering the chat surface. Every skip the
 * render loop performs is enumerated here and the loop calls these; a new
 * hidden kind is added HERE, never inline.
 */
import type { ChatMessage } from './types';
import { isRetiredDevNoticeMessage } from './utils';
import { isActivePendingShapedMessage } from './turnIndicator';
import { isLifecycleMessage, lifecycleFacts } from '../../services/groupThreads/threadLifecycle';

/** The synthetic user prompt behind the onboarding welcome — only the reply shows. */
export const ONBOARDING_WELCOME_SOURCE_TAG = 'system:onboarding_welcome';

/**
 * A non-user message with no text, no html, no attachments, no action
 * buttons and no title: the artifact of an interrupted or empty stream,
 * chrome rather than content (generalized 2026-08-19). User messages are
 * never contentless by this rule — an empty user bubble would hide what the
 * person believes they sent.
 */
export function isContentlessAssistantMessage(message: ChatMessage): boolean {
  if (!message || message.isUser) return false;
  const text = typeof message.text === 'string' ? message.text.trim() : '';
  const html = typeof message.html === 'string' ? message.html.trim() : '';
  const wf = typeof message.workflowActionButtonsHtml === 'string' ? message.workflowActionButtonsHtml.trim() : '';
  const title = message.title !== undefined && message.title !== null ? String(message.title).trim() : '';
  return (
    text === ''
    && html === ''
    && !(message.attachments && message.attachments.length > 0)
    && !(message.attachmentsSerialized && message.attachmentsSerialized.length > 0)
    && !(message.actionButtons && message.actionButtons.length > 0)
    && wf === ''
    && title === ''
  );
}

/**
 * Does the transcript skip this row? True for every kind the render loop
 * returns on without drawing: context-only rows, stored retired dev notices,
 * a lifecycle doc whose facts do not parse, the onboarding welcome's own
 * prompt, an active pending-shaped row (hoisted into the one overlay lane),
 * and a contentless assistant message.
 */
export function isHiddenTranscriptRow(message: ChatMessage | null | undefined): boolean {
  if (!message) return true;
  if (message.isContextOnly) return true;
  if (isRetiredDevNoticeMessage(message)) return true;
  if (isLifecycleMessage(message)) return !lifecycleFacts(message);
  if (message.sourceTag === ONBOARDING_WELCOME_SOURCE_TAG && message.isUser) return true;
  if (isActivePendingShapedMessage(message)) return true;
  if (isContentlessAssistantMessage(message)) return true;
  return false;
}
